import {
  Card,
  SecondaryDescriptionText,
  VerticalSpace,
} from '@brokerbay/design-components';
import { useHistory } from 'react-router';
import { UserSuggestedUpdateType, UserSuggestedUpdateStatus } from '@bb/users';
import errorReporter from '@app/errorReporter';
import { UserSuggestedUpdateController } from 'api';
import { breakpoints, Modal, Text, Title, Button, message } from 'components';
import {
  AuthContext,
  OnboardingWelcomeModalContext,
  UserSuggestedEmailModalContext,
} from 'contexts';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { IUserSuggestedUpdateGetPendingSuggestionsResponse } from '@/app-package';

// Note: Modal currently assumes email is the only update type
export const UserSuggestedEmailModal = () => {
  const history = useHistory();
  const { auth, setAuth } = useContext(AuthContext);
  const { isDoneWelcomeFlow } = useContext(OnboardingWelcomeModalContext);
  const { openModal, closeModal, visible } = useContext(
    UserSuggestedEmailModalContext,
  );

  const [isWorking, setIsWorking] = useState(false);
  const [suggestedEmailUpdate, setSuggestedEmailUpdate] =
    useState<IUserSuggestedUpdateGetPendingSuggestionsResponse[number]>(null);

  const handleClose = () => {
    closeModal();
  };

  const handleKeepPrevious = async () => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      await UserSuggestedUpdateController.dismissUpdate({
        suggestedUpdateId: suggestedEmailUpdate.id,
      });
    } catch (err) {
      let msg = err.message;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(err);
      message.error(`Failed to keep previous email: ${msg}`);
    } finally {
      setIsWorking(false);
      closeModal();
    }
  };

  const handleUpdate = async () => {
    if (isWorking) return;
    setIsWorking(true);
    try {
      const result = await UserSuggestedUpdateController.acceptUpdate({
        suggestedUpdateId: suggestedEmailUpdate.id,
      });
      if (result.status === UserSuggestedUpdateStatus.failed) {
        throw new Error(result.reason);
      } else {
        message.success('Email updated');
        setAuth(prev => ({ ...prev, email: result.newValue as string }));
        if (history.location.pathname !== '/profile') {
          history.push('/profile');
        }
      }
    } catch (err) {
      let msg = err.message;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(err);
      message.error(`Failed to update email: ${msg}`);
    } finally {
      setIsWorking(false);
      closeModal();
    }
  };

  useEffect(() => {
    if (!auth?.id || !isDoneWelcomeFlow || suggestedEmailUpdate) return;
    let active = true;

    const fetchPendingUserSuggestions = async () => {
      const suggestions =
        await UserSuggestedUpdateController.getPendingSuggestions(
          { userId: auth.id },
          { type: [UserSuggestedUpdateType.email] },
        );
      if (!active || !suggestions.length) return;
      // Should have 1 suggestion max but sort by createdAt just in case
      suggestions.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      setSuggestedEmailUpdate(suggestions[0]);
    };

    fetchPendingUserSuggestions();

    return () => {
      active = false;
    };
  }, [auth?.id, isDoneWelcomeFlow, suggestedEmailUpdate]);

  useEffect(() => {
    if (suggestedEmailUpdate) openModal();
  }, [suggestedEmailUpdate, openModal]);

  if (!suggestedEmailUpdate) return null;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      cancelText="Close"
      title={'Update Email'}
      footer={
        <div>
          <Button loading={isWorking} onClick={handleKeepPrevious}>
            Keep Previous
          </Button>
          <Button loading={isWorking} onClick={handleUpdate} type="primary">
            Update Email
          </Button>
        </div>
      }
      width={breakpoints.sm}
    >
      <StyledVerticalSpace size="large">
        <Title textAlign="center">Would you like to update your email?</Title>

        <Card>
          <VerticalSpace>
            <Text strong>New</Text>
            <Text>{suggestedEmailUpdate.newValue}</Text>
            <SecondaryDescriptionText>
              We&apos;ve detected a change to your email on the MLS. This email
              is different than the one you have on BrokerBay. Note: This will
              become your new login for BrokerBay if you choose to update and
              all notifications will be sent to that email going forward.
            </SecondaryDescriptionText>
          </VerticalSpace>
        </Card>

        <Card>
          <VerticalSpace>
            <Text strong>Previous</Text>
            <Text>{suggestedEmailUpdate.prevValue}</Text>
          </VerticalSpace>
        </Card>
      </StyledVerticalSpace>
    </Modal>
  );
};

const StyledVerticalSpace = styled(VerticalSpace)`
  text-align: center;
`;
